import api from './api';
import { validateId } from '../utils/validation';
import { apiRoutes } from './apiConfig';

export const apiService = {
  // Internal helper to get the base URL
  _getBaseUrl: (tableType, tableName) => {
    return tableType === 'system'
      ? `/api/system/${tableName}`
      : `/api/tbl/${tableName}`;
  },

  // Authentication related API calls
  login: async (email, password) => {
    return await api.post(apiRoutes.auth.login.path, { email, password });
  },

  register: async (email, password, firstName) => {
    return await api.post('/auth/local/register', {
      email,
      password,
      firstName,
      language: navigator.language
    });
  },

  logout: async (clientKey = null) => {
    // Backend requires client_key to properly revoke refresh token
    // If not provided, we don't send it (backend will handle gracefully)
    const body = clientKey ? { client_key: clientKey } : {};
    return await api.post(apiRoutes.auth.logout.path, body);
  },

  socialAuth: async (provider, data) => {
    return await api.post(`/auth/${provider}/callback`, data);
  },

  // Server health check - simple fetch for checking connectivity
  checkServerHealth: async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(apiRoutes.server.health.path, {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-store'
      });
      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      clearTimeout(timeoutId);
      return false;
    }
  },

  // Order related API calls
  createPaymentOrder: async (orderData) => {
    if (orderData.userId) {
      validateId(orderData.userId, { allowLegacy: true });
    }
    return await api.post(apiRoutes.payment.createOrder.path, orderData);
  },

  // New method for creating subscription orders
  createSubscriptionOrder: async (orderData) => {
    if (orderData.userId) {
      validateId(orderData.userId, { allowLegacy: true });
    }
    return await api.post(apiRoutes.payment.createSubscriptionOrder.path, orderData);
  },

  getOrderDetails: async (orderId) => {
    validateId(orderId);
    const url = apiRoutes.payment.getOrderDetails.path.replace(':id', orderId);
    return await api.get(url);
  },

  cancelOrder: async (orderId) => {
    validateId(orderId);
    return await api.post(`/api/payment/${orderId}/cancel-order`);
  },

  cancelSubscription: async (subscriptionId, options = {}) => {
    validateId(subscriptionId);
    const { cancelImmediately = false, reason = '' } = options;
    return await api.post(`/api/payment/cancel-subscription/${subscriptionId}`, {
      cancelImmediately,
      reason
    });
  },

  // Get subscription details including current period, status, etc.
  getSubscriptionDetails: async (subscriptionId) => {
    validateId(subscriptionId);
    return await api.get(`/api/payment/subscription/${subscriptionId}`);
  },

  // Pause a subscription (alternative to full cancellation)
  pauseSubscription: async (subscriptionId, pauseDuration) => {
    validateId(subscriptionId);
    return await api.post(`/api/payment/pause-subscription/${subscriptionId}`, {
      pauseDuration // Duration in months
    });
  },

  // Resume a paused subscription
  resumeSubscription: async (subscriptionId) => {
    validateId(subscriptionId);
    return await api.post(`/api/payment/resume-subscription/${subscriptionId}`);
  },

  // Get site entitlement summary for dashboard
  getEntitlementSummary: async () => {
    return await api.get('/api/payment/entitlement-summary');
  },

  // Update user preferences (currency, language)
  updateUserPreferences: async (preferences) => {
    return await api.put('/api/user/preferences', preferences);
  },

  fetchOrders: async (userId = null) => {
    const params = {};
    if (userId) {
      validateId(userId, { allowLegacy: true });
      params.userId = userId;
    }
    return await api.get('/api/orders/', { params });
  },

  fetchOrderById: async (orderId) => {
    validateId(orderId);
    return await api.get(`/api/orders/${orderId}`);
  },

  // Product related API calls
  fetchProducts: async (preferredCurrency) => {
    return await api.get('/api/products/GetAll', { params: { preferredCurrency } });
  },

  fetchProductById: async (id, preferredCurrency) => {
    validateId(id);
    return await api.get(`/api/products/${id}`, { params: { preferredCurrency } });
  },

  fetchProductsWithQuery: async (query, preferredCurrency) => {
    return await api.get('/api/products/GetWhere', { params: { ...query, preferredCurrency } });
  },

  // Table Editor API functions
  fetchTableData: async (tableType, tableName, page, params = {}) => {
    try {
      const baseUrl = apiService._getBaseUrl(tableType, tableName);

      // Use GetWhere endpoint if search or filters are provided
      const hasSearchOrFilters = params.search || Object.keys(params).some(key => key !== 'search' && params[key]);
      const endpoint = hasSearchOrFilters ? `${baseUrl}/GetWhere` : baseUrl;

      const queryParams = {
        page,
        ...params
      };

      const response = await api.get(endpoint, {
        params: queryParams
      });

      if (response.headers['content-type']?.includes('text/html')) {
        throw new Error('Received HTML response instead of JSON');
      }
      return response;

    } catch (error) {
      throw error;
    }
  },

  // Fetch a single record by ID
  fetchRecordById: async (tableType, tableName, id) => {
    try {
      validateId(id, { allowLegacy: true });
      const baseUrl = apiService._getBaseUrl(tableType, tableName);

      // User table uses /GetById/:publicId, other tables use /:id
      const isUserTable = tableType === 'system' && tableName === 'User';
      const endpoint = isUserTable
        ? `${baseUrl}/GetById/${id}`  // User: /api/system/User/GetById/{uuid}
        : `${baseUrl}/${id}`;         // Others: /api/system/Table/{id}

      const response = await api.get(endpoint);
      return response?.data?.data || response?.data;
    } catch (error) {
      throw error;
    }
  },

  updateRecord: async (tableType, tableName, id, data) => {
    try {
      validateId(id, { allowLegacy: true });
      const baseUrl = apiService._getBaseUrl(tableType, tableName);
      return await api.put(`${baseUrl}/${id}`, data);
    } catch (error) {
      throw error;
    }
  },

  createRecord: async (tableType, tableName, data) => {
    const baseUrl = apiService._getBaseUrl(tableType, tableName);
    return await api.post(baseUrl, data);
  },

  deleteRecord: async (tableType, tableName, id) => {
    validateId(id, { allowLegacy: true });
    const baseUrl = apiService._getBaseUrl(tableType, tableName);
    return await api.delete(`${baseUrl}/${id}`);
  },

  // Submit contact form message
  submitContactMessage: async (formData) => {
    return await api.post('/api/system/ContactMessage/', formData);
  },

  // Sync data with server
  sync: async (lastSyncTime, localChanges, otherChanges) => {
    return await api.post('/api/sync/', {
      lastSyncTimestamp: lastSyncTime,
      localChanges: localChanges,
      otherChanges: otherChanges
    });
  },

  // Send verification email
  // Returns { success: true } or throws an error
  sendVerificationEmail: async (userId) => {
    validateId(userId, { allowLegacy: true });
    await api.post('/api/auth/sendVerifyEmailLink', {
      id: userId
    });
    // Trigger sync to update local state (don't await, fire and forget)
    // Dynamic import to avoid circular dependency: apiService -> SyncCoordinator -> syncService -> SyncServiceImpl -> apiService
    import('./SyncCoordinator').then(({ default: getSyncCoordinator }) => {
      const syncCoordinator = getSyncCoordinator();
      syncCoordinator.triggerSync().catch(err => {
        //console.warn('Sync after verification email failed:', err);
      });
    });
    return { success: true };
  },

  // Verify email with code
  verifyEmail: async (id, code) => {
    validateId(id, { allowLegacy: true });
    return await api.post('/api/auth/verifyEmail', { id, code });
  },

  // Verify reset code
  verifyResetCode: async (id, code) => {
    validateId(id, { allowLegacy: true });
    return await api.post('/api/auth/verifyResetCode', { id, code });
  },

  // Reset password
  resetPassword: async (id, code, newPassword) => {
    validateId(id, { allowLegacy: true });
    return await api.post('/api/auth/resetPassword', { id, code, newPassword });
  },

  // Handle password reset
  handlePasswordReset: async (selectedUserId, oldPassword, newPassword) => {
    try {
      validateId(selectedUserId, { allowLegacy: true });
      await api.post('/api/auth/resetPassword', {
        id: selectedUserId,
        oldPwd: oldPassword,
        newPwd: newPassword
      });
      alert('Password reset successful');
    } catch (error) {
      alert('Failed to reset password');
    }
  },

  handleAdminPasswordReset: async (selectedUserId, password) => {
    try {
      validateId(selectedUserId, { allowLegacy: true });
      await api.post('/api/auth/resetPasswordForce', {
        id: selectedUserId,
        pwd: password
      });
      alert('Password reset successful');
    } catch (error) {
      alert('Failed to reset password');
    }
  },

  // Get server health metrics
  getServerHealth: async () => {
    return await api.get('/api/serverHealth');
  },

  // User Assets API methods
  getUserAssets: async (params = {}) => {
    const { page = 1, limit = 20, type, search, folderId } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(type && { type }),
      ...(search && { search }),
      ...(folderId != null && { folderId: folderId.toString() })
    });
    return await api.get(`/api/userAssets/?${queryParams}`);
  },

  getUserAssetById: async (id, options = {}) => {
    try {
      const { asBlob = false } = options;
      const response = await api.get(`/api/userAssets/file/${id}`, {
        ...(asBlob && { responseType: 'blob' })
      });
      return asBlob ? response.data : response;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Asset not found or access denied');
      }
      throw error;
    }
  },

  uploadUserAsset: async (formData) => {
    try {
      return await api.post('/api/userAssets/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data.error || 'Invalid upload request');
      }
      throw error;
    }
  },

  updateUserAsset: async (id, data) => {
    try {
      const response = await api.put(`/api/userAssets/${id}`, data);

      if (!response) {
        throw new Error('Server returned empty response');
      }

      // For asset moves, verify the folderId field exists
      if (data.folderId !== undefined && typeof response.folderId === 'undefined') {
        throw new Error('Invalid server response - missing folderId');
      }

      // For root moves (null folderId)
      if (data.folderId === null && response.folderId !== null) {
        throw new Error('Server did not apply the folder update');
      }

      // For folder moves (non-null folderId)
      if (data.folderId !== null && response.folderId !== data.folderId) {
        throw new Error('Server did not apply the folder update');
      }

      return response;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Asset not found or access denied');
      }

      if (error.message === 'Server did not apply the folder update') {
        throw new Error('Failed to move asset - server did not update the folder');
      }

      throw error;
    }
  },

  deleteUserAsset: async (id) => {
    try {
      return await api.delete(`/api/userAssets/${id}`);
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Asset not found or access denied');
      }
      throw error;
    }
  },

  shareUserAsset: async (id, userIds) => {
    try {
      return await api.post(`/api/userAssets/${id}/share`, { userIds });
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error('Invalid sharing request');
      } else if (error.response?.status === 404) {
        throw new Error('Asset not found or access denied');
      }
      throw error;
    }
  },

  makeAssetPublic: async (id, isPublic) => {
    try {
      return await api.put(`/api/userAssets/${id}/public`, { isPublic });
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error('Invalid visibility request');
      } else if (error.response?.status === 404) {
        throw new Error('Asset not found or access denied');
      }
      throw error;
    }
  },

  // User data export
  exportUserData: async () => {
    return await api.get('/api/user/export', {
      responseType: 'blob' // Important for file download
    });
  },

  // Delete user account
  deleteUserAccount: async (password) => {
    return await api.post('/api/user/account/delete', { password });
  },

  // Request account undelete verification code
  requestAccountUndelete: async (email) => {
    try {
      return await api.post('/api/user/account/undelete/request', { email });
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data.message || 'Invalid request');
      } else if (error.response?.status === 404) {
        // Return a generic message for security reasons
        return {
          success: true,
          message: 'If your account exists and is eligible for recovery, you will receive an email with instructions'
        };
      }
      throw error;
    }
  },

  // Undelete user account with verification code
  undeleteAccount: async (email, code) => {
    try {
      return await api.post('/api/user/account/undelete', { email, code });
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data.message || 'Invalid verification code');
      } else if (error.response?.status === 404) {
        throw new Error('Account not found or not eligible for recovery');
      }
      throw error;
    }
  }
};
